import { useLocation } from "react-router-dom";
import { Chrome, ShieldCheck, Activity, Radar, ShieldX } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const location = useLocation();
  const accessDenied = location.state?.accessDenied === true;

  const handleLogin = () => {
    const params = new URLSearchParams({
      client_id: "159137672389-l18s2qe5st9rs5g3pj2aqdv36j3po6b2.apps.googleusercontent.com",
      redirect_uri: window.location.origin + "/auth/callback",
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      prompt: "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const heroBg =
    "https://static.prod-images.emergentagent.com/jobs/fcf3e62b-e3c4-4332-b9ef-bd295ccb9535/images/d8b785c82d0fa78c54fae70fef5b99ec27c50c59950d0611c2c85d86e20fe54d.png";

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0B0E14] text-white">
      {/* Hero background */}
      <div
        className="absolute inset-0 opacity-60"
        style={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E14]/95 via-[#0B0E14]/80 to-[#0B0E14]/30" />
      <div className="absolute inset-0 bg-grid opacity-40" />

      <div className="relative z-10">
        {/* Top bar */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-orange-500/50 flex items-center justify-center bg-orange-500/5">
              <Radar className="w-4 h-4 text-orange-400" />
            </div>
            <div>
              <div className="font-display font-black text-lg tracking-tight leading-none">
                EXEC<span className="text-orange-400">/</span>HANGAR
              </div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 mt-1">Card Cooldown Tracker</div>
            </div>
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 hidden sm:block">
            UEE // mobiGlas v4.7
          </div>
        </div>

        {/* Main */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 animate-slide-up">
            <div className="text-[12px] tracking-[0.4em] uppercase text-orange-400 mb-6">
              / Pyro-Stanton Directive · Clearance AE-01
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
              Track every card.
              <br />
              <span className="text-orange-500" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>Own the hangar.</span>
            </h1>
            <p className="text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed mb-10">
              A tactical countdown board for every access card on your route to unlocking the
              Executive Hangar. Know exactly when each rotation resets — down to the second.
            </p>

            {accessDenied && (
              <div
                className="relative mb-8 border border-orange-500/50 bg-orange-500/10 p-4 max-w-xl"
                data-testid="access-denied-banner"
              >
                <div className="flex items-start gap-3">
                  <ShieldX className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display font-bold text-orange-400 text-sm tracking-wide uppercase mb-1">
                      Access Denied
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      Your email is not whitelisted for this app. Please contact{" "}
                      <span className="text-orange-400 font-semibold">AFriendOfBlue</span> on Discord
                      to request access.
                    </p>
                  </div>
                </div>
                <span className="absolute -top-1 -left-1 w-2 h-2 border-t border-l border-orange-400/70" />
                <span className="absolute -top-1 -right-1 w-2 h-2 border-t border-r border-orange-400/70" />
                <span className="absolute -bottom-1 -left-1 w-2 h-2 border-b border-l border-orange-400/70" />
                <span className="absolute -bottom-1 -right-1 w-2 h-2 border-b border-r border-orange-400/70" />
              </div>
            )}

            <button
              onClick={handleLogin}
              data-testid="google-signin-button"
              className="group relative inline-flex items-center gap-3 px-6 py-4 border border-orange-500 bg-orange-500/10 text-orange-400 hover:bg-orange-500 hover:text-black transition-colors tracking-widest uppercase text-xs"
            >
              <Chrome className="w-4 h-4" />
              Sign in with Google
              <span className="absolute -top-1 -left-1 w-2 h-2 border-t border-l border-orange-400" />
              <span className="absolute -top-1 -right-1 w-2 h-2 border-t border-r border-orange-400" />
              <span className="absolute -bottom-1 -left-1 w-2 h-2 border-b border-l border-orange-400" />
              <span className="absolute -bottom-1 -right-1 w-2 h-2 border-b border-r border-orange-400" />
            </button>

            <div className="mt-8 text-[12px] tracking-[0.3em] uppercase text-slate-500">
              No passwords · Secure OAuth handshake
            </div>
          </div>

          {/* Feature panel */}
          <div className="lg:col-span-5 animate-slide-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative border border-orange-500/25 bg-[#121822]/80 backdrop-blur-sm p-6 sm:p-8 corner-tl corner-tr corner-bl corner-br">
              <div className="text-[10px] tracking-[0.3em] uppercase text-orange-400 mb-4">Mission Brief</div>
              <div className="space-y-5">
                <Feature
                  icon={<Activity className="w-4 h-4 text-orange-400" />}
                  title="Real-time countdowns"
                  body="Multi-card live timers synced to UTC. Know when every card rotates back."
                />
                <Feature
                  icon={<Radar className="w-4 h-4 text-orange-400" />}
                  title="Custom card roster"
                  body="Add, edit and delete cards on your personal tracking board."
                />
                <Feature
                  icon={<ShieldCheck className="w-4 h-4 text-orange-400" />}
                  title="Cloud synced"
                  body="Your board follows you across devices. Sign in once, track anywhere."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="flex gap-4">
      <div className="shrink-0 w-9 h-9 border border-orange-500/30 flex items-center justify-center bg-orange-500/5">
        {icon}
      </div>
      <div>
        <div className="font-display font-bold text-white text-base mb-0.5">{title}</div>
        <div className="text-sm text-slate-400 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}
